import api from './api';

export interface SecuritySettings {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    expirationDays: number;
    preventReuse: number;
  };
  twoFactorAuth: {
    enabled: boolean;
    required: boolean;
    methods: string[];
  };
  sessionManagement: {
    timeoutMinutes: number;
    idleTimeoutMinutes: number;
    rememberMeDays: number;
    forceLogoutOnPasswordChange: boolean;
    maxConcurrentSessions: number;
  };
  ipWhitelist: {
    enabled: boolean;
    addresses: string[];
  };
  loginAttempts: {
    maxAttempts: number;
    lockoutDurationMinutes: number;
  };
}

// Keycloak-backed auth security, surfaced read-only (password policy) and
// read/write (brute-force detection). Exposed via /system/auth-security.
export interface KcPasswordPolicy {
  raw?: string | null;
  minLength?: number;
  maxLength?: number;
  upperCase?: number;
  lowerCase?: number;
  digits?: number;
  specialChars?: number;
  notUsername?: boolean;
  notEmail?: boolean;
  passwordHistory?: number;
}

export interface KcBruteForceSettings {
  bruteForceProtected: boolean;
  permanentLockout: boolean;
  failureFactor: number;
  waitIncrementSeconds: number;
  maxFailureWaitSeconds: number;
  minimumQuickLoginWaitSeconds: number;
  quickLoginCheckMilliSeconds: number;
  maxDeltaTimeSeconds: number;
}

export interface AuthSecurityResponse {
  passwordPolicy: KcPasswordPolicy;
  bruteForce: KcBruteForceSettings;
}

export type EmailProvider = 'smtp' | 'graph';

export interface GraphMailerConfig {
  tenantId: string;     // Azure tenant (directory) ID
  clientId: string;     // App registration ID
  clientSecret: string;
  senderAddress: string; // Mailbox the app has Mail.Send permission on
}

export interface NotificationSettings {
  email: {
    enabled: boolean;
    provider?: EmailProvider; // defaults to 'smtp' for back-compat
    // SMTP
    smtpServer: string;
    smtpPort: number;
    smtpUser: string;
    smtpPassword: string;
    // Microsoft Graph (App Registration / client_credentials)
    graph?: GraphMailerConfig;
    // Common
    fromAddress: string;
    fromName: string;
    alertTemplateId?: string;
  };
  sms: {
    enabled: boolean;
    provider: string;
    apiKey: string;
    fromNumber: string;
  };
  webhook: {
    enabled: boolean;
    url: string;
    secret: string;
  };
  notificationTypes: {
    deviceOffline: { email: boolean; sms: boolean; webhook: boolean };
    deviceOnline: { email: boolean; sms: boolean; webhook: boolean };
    securityAlert: { email: boolean; sms: boolean; webhook: boolean };
    systemError: { email: boolean; sms: boolean; webhook: boolean };
    maintenance: { email: boolean; sms: boolean; webhook: boolean };
  };
}

export interface BackupSettings {
  enabled: boolean;
  schedule: string;
  scheduleTime: string;
  retentionDays: number;
}

export interface BackupJob {
  id: string;
  type: 'backup' | 'restore';
  status: 'pending' | 'in_progress' | 'dry_run_complete' | 'restoring' | 'completed' | 'failed';
  fileName: string | null;
  fileSize: number;
  manifest: any;
  dryRunResult: any;
  errorMessage: string | null;
  sourceBackupId: string | null;
  createdBy: string | null;
  createdAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface BackupData {
  settings: BackupSettings;
  recentBackups: BackupJob[];
  nextBackup: string | null;
}

export interface AlertRule {
  id: string;
  name: string;
  enabled: boolean;
  condition: string;
  duration: string;
  severity: 'low' | 'medium' | 'high';
  notificationChannels: string[];
}

export interface AlertSettings {
  rules: AlertRule[];
  thresholds: {
    cpu: { warning: number; critical: number };
    memory: { warning: number; critical: number };
    disk: { warning: number; critical: number };
    network: { warning: number; critical: number };
    latency: { warning: number; critical: number };
  };
}

export interface GeneralSettings {
  systemName: string;
  companyName: string;
  platformUrl: string;
  supportEmail?: string;
  appDownloadIos: string;
  appDownloadAndroid: string;
  appDownloadMacos: string;
  appDownloadWindows: string;
  timeZone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  language: string;
  apiBaseUrl: string;
  apiTimeout: number;
  rateLimiting: {
    enabled: boolean;
    maxRequestsPerMinute: number;
  };
  sessionManagement: {
    timeoutMinutes: number;
    idleTimeoutMinutes: number;
    rememberMeDays: number;
    forceLogoutOnPasswordChange: boolean;
  };
}

export interface DiscoverySettings {
  // MQTT Configuration
  mqttHost: string;
  mqttPort: number;
  mqttTlsPort: number;

  // Orchestrator API
  orchestratorCode?: string;
  apiHost: string;
  apiPort: number;
  apiProtocol: string;
  discoveryTtl: number;

  // SSH Bastion Configuration
  sshBastionHost: string;
  sshBastionPort: number;
  sshBastionUser: string;

  // Observability Configuration
  lokiUrl: string;
  prometheusPushgatewayUrl: string;

  // RADIUS Configuration
  radiusHost: string;
  radiusPort: number;

  // Network Services
  dnsServers: string;  // Comma-separated
  ntpServers: string;  // Comma-separated

  // Private DNS
  dnsZoneSuffix: string;  // Zone suffix for CoreDNS (e.g., "vpn.securelink.dev")

  // Edge Provisioning
  autoApproveEdges: boolean;
}

export interface AppDiscoverySettings {
  // Keycloak Configuration for VPN Apps
  keycloakUrl: string;
  keycloakRealm: string;
  keycloakClientId: string;

  // API Configuration
  apiUrl: string;

  // VPN Configuration
  vpnProtocol: string;
  vpnPort: number;
  persistentKeepalive: number;
}

const systemService = {
  /**
   * Get security settings
   */
  getSecurity: async (): Promise<SecuritySettings> => {
    const response = await api.get('/system/security');
    return (response as any).data;
  },

  /**
   * Update security settings
   */
  updateSecurity: async (data: SecuritySettings): Promise<SecuritySettings> => {
    const response = await api.put('/system/security', data);
    return (response as any).data;
  },

  /**
   * Read Keycloak-backed auth security (password policy + brute-force settings).
   */
  getAuthSecurity: async (): Promise<AuthSecurityResponse> => {
    const response = await api.get('/system/auth-security');
    return (response as any).data;
  },

  /**
   * Update the brute-force detection settings on the Keycloak realm.
   * Password policy is read-only from the UI — change via KC admin console.
   */
  updateAuthSecurity: async (bruteForce: Partial<KcBruteForceSettings>): Promise<AuthSecurityResponse> => {
    const response = await api.put('/system/auth-security', { bruteForce });
    return (response as any).data;
  },

  /**
   * Get notification settings
   */
  getNotifications: async (): Promise<NotificationSettings> => {
    const response = await api.get('/system/notifications');
    return (response as any).data;
  },

  /**
   * Update notification settings
   */
  updateNotifications: async (data: NotificationSettings): Promise<NotificationSettings> => {
    const response = await api.put('/system/notifications', data);
    return (response as any).data;
  },

  /**
   * Get backup settings
   */
  getBackup: async (): Promise<BackupData> => {
    const response = await api.get('/system/backup');
    return (response as any).data;
  },

  /**
   * Update backup settings
   */
  updateBackup: async (data: BackupSettings): Promise<BackupData> => {
    const response = await api.put('/system/backup', data);
    return (response as any).data;
  },

  /**
   * Create manual backup
   */
  createBackup: async (): Promise<{ jobId: string; status: string; message: string }> => {
    const response = await api.post('/system/backup/create');
    return (response as any).data;
  },

  /**
   * Initiate restore from backup (starts dry-run)
   */
  restoreBackup: async (backupId: string): Promise<{ jobId: string; status: string }> => {
    const response = await api.post(`/system/backup/restore/${backupId}`);
    return (response as any).data;
  },

  /**
   * Confirm restore after dry-run
   */
  confirmRestore: async (jobId: string): Promise<{ status: string }> => {
    const response = await api.post(`/system/backup/restore/${jobId}/confirm`);
    return (response as any).data;
  },

  /**
   * Get a single backup job by ID (for polling)
   */
  getBackupJob: async (jobId: string): Promise<BackupJob> => {
    const response = await api.get(`/system/backup/job/${jobId}`);
    return (response as any).data;
  },

  /**
   * Download a backup zip file.
   *
   * The shared axios response interceptor returns `response.data`, so when
   * `responseType: 'blob'` is set the awaited value is already the Blob.
   * Reading `.data` on it yielded undefined — `new Blob([undefined])` then
   * wrote a 9-byte file containing the string "undefined", which the user
   * saw as a corrupted backup. Use the Blob directly.
   */
  downloadBackup: async (backupId: string): Promise<void> => {
    const blob = (await api.get(`/system/backup/download/${backupId}`, { responseType: 'blob' })) as unknown as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup-${backupId}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * Delete a backup
   */
  deleteBackup: async (backupId: string): Promise<void> => {
    await api.delete(`/system/backup/${backupId}`);
  },

  /**
   * Get alert settings
   */
  getAlerts: async (): Promise<AlertSettings> => {
    const response = await api.get('/system/alerts');
    return (response as any).data;
  },

  /**
   * Update alert settings
   */
  updateAlerts: async (data: AlertSettings): Promise<AlertSettings> => {
    const response = await api.put('/system/alerts', data);
    return (response as any).data;
  },

  /**
   * Get general settings
   */
  getGeneral: async (): Promise<GeneralSettings> => {
    const response = await api.get('/system/general');
    return (response as any).data;
  },

  /**
   * Update general settings
   */
  updateGeneral: async (data: GeneralSettings): Promise<GeneralSettings> => {
    const response = await api.put('/system/general', data);
    return (response as any).data;
  },

  /**
   * Register with Global VSA
   */
  /**
   * Save Global VSA Orchestrator Credentials
   */
  saveGlobalVsaCredentials: async (data: { clientId: string; clientSecret: string; globalVsaUrl: string }): Promise<any> => {
    const response = await api.put('/system/global-vsa/credentials', data);
    return (response as any).data;
  },

  /**
   * Get Global VSA Orchestrator Credentials (masked secret)
   */
  getGlobalVsaCredentials: async (): Promise<any> => {
    const response = await api.get('/system/global-vsa/credentials');
    return (response as any).data;
  },

  /**
   * Get Global VSA Status
   */
  getGlobalVsaStatus: async (): Promise<any> => {
    const response = await api.get('/system/global-vsa/status');
    return (response as any).data;
  },

  /**
   * Generate deployment token for edge device registration
   * @param tenantId - The tenant ID for the edge
   * @param templateId - The configuration template ID to apply when edge connects
   * @param vppMode - VPP dataplane mode: 'tap' (default), 'afpacket', or 'dpdk'
   */
  generateDeploymentToken: async (tenantId: string, templateId?: string, vppMode?: string): Promise<{
    token: string;
    tenantId: string;
    templateId?: string;
    expiresAt: string;
    expiresIn: string;
  }> => {
    const response = await api.post('/system/global-vsa/generate-deployment-token', { tenantId, templateId, vppMode });
    return (response as any).data;
  },

  /**
   * Get organisation code from Global VSA (cached server-side)
   */
  getOrgCode: async (): Promise<string> => {
    const response = await api.get('/system/org-code');
    return (response as any).data?.orgCode || '';
  },

  /**
   * Get discovery settings
   */
  getDiscovery: async (): Promise<DiscoverySettings> => {
    const response = await api.get('/system/discovery');
    return (response as any).data;
  },

  /**
   * Update discovery settings
   */
  updateDiscovery: async (data: DiscoverySettings): Promise<DiscoverySettings> => {
    const response = await api.put('/system/discovery', data);
    return (response as any).data;
  },

  /**
   * Get VPN app discovery settings
   */
  getAppDiscovery: async (): Promise<AppDiscoverySettings> => {
    const response = await api.get('/system/app-discovery');
    return (response as any).data;
  },

  /**
   * Update VPN app discovery settings
   */
  updateAppDiscovery: async (data: AppDiscoverySettings): Promise<AppDiscoverySettings> => {
    const response = await api.put('/system/app-discovery', data);
    return (response as any).data;
  },

  /**
   * Get default data configuration
   */
  getDefaults: async (): Promise<any> => {
    const response = await api.get('/system/defaults');
    return (response as any).data;
  },

  /**
   * Get feature flags for current tenant
   */
  getFeatureFlags: async (): Promise<Record<string, boolean>> => {
    const response = await api.get('/system/feature-flags');
    return (response as any).data;
  },
};

export default systemService;

